import React, { Ref } from 'react';

import { getSearchTypeMessage } from './CommandPaletteRenderers.view';
import { ResultsList } from './CommandPaletteResults.view';

import { CommandPaletteSearchTypes, Needle } from '../../types/commandPalette.types';

import { TestID } from '../../testID';

import './CommandPalette.css';

const CommandPaletteHint: React.FC = () => (
  <div className="command-palette__hint" data-testid={TestID.CommandPaletteHint}>
    <span>
      Toggle this object palette with <kbd>Ctrl</kbd> + <kbd>Space</kbd>
    </span>
  </div>
);

export const CommandPaletteSearchPanel: React.FC<{
  results: Needle[];
  searchType: CommandPaletteSearchTypes;
  input: string;
  includeInternalObjects: boolean;
  selectedResult?: Needle;
  selIdx: number;
  rootEl?: Ref<HTMLFormElement> | undefined;
  inputRef?: Ref<HTMLInputElement> | undefined;
  isShowingRecent: boolean;
  isShowingNothingFound: boolean;
  handleSubmit: (results: Needle[], ev: React.FormEvent<HTMLFormElement>) => void;
  handleOnChange: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  handleIncludeInternalObjectsChange: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemovePrefixSearch: (ev: React.KeyboardEvent<HTMLInputElement>) => void;
  handleNeedleClick: (needle: Needle) => (ev: React.MouseEvent<HTMLLIElement>) => void;
  darkModeActive?: boolean;
}> = ({
  results,
  searchType,
  input = '',
  includeInternalObjects,
  selectedResult,
  selIdx,
  rootEl,
  inputRef,
  isShowingRecent,
  isShowingNothingFound,
  handleSubmit,
  handleOnChange,
  handleIncludeInternalObjectsChange,
  handleRemovePrefixSearch,
  handleNeedleClick,
  darkModeActive,
}) => (
  <form
    className={darkModeActive ? 'command-palette dark' : 'command-palette'}
    ref={rootEl}
    onSubmit={(ev) => handleSubmit(results, ev)}
    autoComplete="off"
    data-testid={TestID.CommandPalette}
  >
    <div className="command-palette__input">
      {getSearchTypeMessage(searchType)}
      <input
        autoFocus
        id="command-palette-input-field"
        ref={inputRef}
        className="command-palette__input-field"
        onChange={handleOnChange}
        placeholder="Search…"
        value={input}
        onKeyDown={handleRemovePrefixSearch}
        autoComplete="off"
        data-testid={TestID.CommandPaletteSearchInput}
      />
      <span aria-live="assertive" aria-atomic="true" className="visually-hidden">
        {selectedResult && (
          <span>
            {selectedResult.renderer({
              id: selectedResult.item.id,
              name: selectedResult.item?.name,
              type: selectedResult.type,
            })}
            Submit form to navigate
          </span>
        )}
      </span>
      <label
        className={`command-palette__filter-toggle${includeInternalObjects ? ' command-palette__filter-toggle--active' : ''}`}
        data-testid={TestID.CommandPaletteIncludeInternalObjectsToggle}
        title="Include internal objects in search results"
      >
        <input
          type="checkbox"
          checked={includeInternalObjects}
          onChange={handleIncludeInternalObjectsChange}
        />
        <span>Include internal objects</span>
      </label>
    </div>
    {isShowingRecent && (
      <span aria-live="assertive" className="command-palette__feedback">
        Recent searches
      </span>
    )}
    {isShowingNothingFound && (
      <div
        aria-live="assertive"
        className="command-palette__feedback"
        data-testid={TestID.CommandPaletteNothingFound}
      >
        Nothing found!
      </div>
    )}
    <ResultsList results={results} handleNeedleClick={handleNeedleClick} selIdx={selIdx} />
    {input.length === 0 && <CommandPaletteHint />}
  </form>
);
